import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { TrendingUp } from 'lucide-react'
import { formatRand, formatRandCompact, type Period, type TrendPoint } from '../../utils/performance'

const SERIES = {
  driver_net: { label: 'Driver net', color: 'hsl(var(--perf-driver-net))' },
  uber_gross: { label: 'Uber gross', color: 'hsl(var(--perf-uber-gross))' },
  petrol_cost: { label: 'Petrol cost', color: 'hsl(var(--perf-petrol))' },
}

function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-popover-foreground">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: p.color }}
            />
            {p.name}
          </span>
          <span className="font-medium tabular-nums text-popover-foreground">
            {formatRand(p.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function TrendChart({
  data,
  period,
  loading,
}: {
  data: TrendPoint[]
  period: Period
  loading: boolean
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-retool-sm">
      <div className="mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">
          Financial trend
          <span className="ml-1 font-normal text-muted-foreground">
            · by {period === 'total' ? 'total' : period}
          </span>
        </h2>
      </div>
      {loading ? (
        <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : data.length === 0 ? (
        <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
          No data for the selected filters.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="bucket"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              minTickGap={16}
            />
            <YAxis
              tickFormatter={formatRandCompact}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <Tooltip content={<TrendTooltip />} cursor={{ fill: 'hsl(var(--accent))', opacity: 0.4 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              dataKey="driver_net"
              name={SERIES.driver_net.label}
              fill={SERIES.driver_net.color}
              radius={[3, 3, 0, 0]}
              maxBarSize={40}
            />
            <Bar
              dataKey="petrol_cost"
              name={SERIES.petrol_cost.label}
              fill={SERIES.petrol_cost.color}
              radius={[3, 3, 0, 0]}
              maxBarSize={40}
            />
            <Line
              type="monotone"
              dataKey="uber_gross"
              name={SERIES.uber_gross.label}
              stroke={SERIES.uber_gross.color}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
